<template>
  <div>

    <div class="rate-set">

      <!-- TODO: add :key -->
      <RateForm
        v-for="(defaultRate, index) in v$.defaultRates.$model"
        v-model="v$.defaultRates.$model[index]"
        :all-default-rates="v$.defaultRates.$model"
        :only-channel-facility-ids="onlyChannelFacilityIds"
        :tiered="tiered"
        :readonly="readonly"
        :deletable="v$.defaultRates.$model.length > 1"
        @delete="deleteDefaultRate(index)"
        :show-ids="canSeeIds"
        :max-tier-duration-in-hours="maxTierDurationInHours"
      />

      <div v-if="tiered" class="rate-set-item">

        <div class="rate-set-item-error">
          <template v-if="v$.defaultRates.$errors.length">
            <span v-for="error in v$.defaultRates.$errors">{{ error.$message }}<br></span>
          </template>
        </div>

        <div class="rate-set-item-tool">
          <v-btn
            v-if="tiered && !readonly"
            icon="$create"
            title="Add rate tier"
            @click="addDefaultRate"
          />
        </div>

      </div>

    </div>

  </div>
</template>

<script setup>

/**
 * Represents a collection of DefaultRates for a single LP, along with any
 * associated ChannelDefaultRates.
 * 
 * /utils/rateSetUtils.js provides helper functions for building and saving
 * the formState structure that this component expects as its v-model.
 * 
 * This component, and its various children and helpers, are one of the
 * messiest and most confusing parts of this app. Sorry about that :/
 */

import { computed, inject } from 'vue'

// components
import RateForm from './RateForm.vue'

// models
import { ChannelFacility } from '../models/ChannelFacility'
import Location from '../models/Location'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'

const props = defineProps({

  modelValue: {
    type: Object,
    required: true,
  },

  // It's assumed that ChannelFacilities for this location will be already
  // loaded into this store before this component is mounted.
  location: {
    type: Location,
    required: true,
  },

  tiered: {
    type: Boolean,
    default: false,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  // If provided, only channel_default_rates belonging to the specified
  // channel_facilities will be shown.
  onlyChannelFacilityIds: {
    type: Array,
    default: null,
  },

  maxTierDurationInHours: {
    type: Number,
    default: null,
  },

})

const $can = inject('$can')
const canSeeIds = $can('seeids')

const emit = defineEmits([
  'update:modelValue',
])

const channelFacilities = computed(() => {
  return ChannelFacility.query().where('facilityID', props.location.facilityID).get()
})

/**
 * Form state and rules
 */

const formState = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// TODO: If we're using a nested component for the individual rateset forms, then we might want to remove validation here.
// But then how do we bubble the validation state up to the main component?

// TODO: validate that there's at least one rate
// TODO: if tiered, validate that each tier in the rate structure has a unique perUnit and perQuantity (convert days to hours before comparing)

const formRules = {
  defaultRates: {
    // TODO: require at least one?
    tierDurationsAreUnique: helpers.withMessage('Can\'t have more than one tier with the same duration', (defaultRates) => {
      const durations = []
      defaultRates.forEach((defaultRate) => {
        if (defaultRate.perUnit && defaultRate.perQuantity) {
          const duration = defaultRate.perUnit === 'day' ? defaultRate.perQuantity * 24 : defaultRate.perQuantity
          durations.push(duration)
        }
      })
      return new Set(durations).size === durations.length
    }),
  },
}

const v$ = useVuelidate(formRules, formState)

// force Vuelidate to consider this validator dirty, so that errors will be populated into v$.$errors instead of v$.$silentErrors
v$.value.defaultRates.$touch()

/**
 * Button handlers
 */

function addDefaultRate() {

  let perUnit = null

  if (formState.value.defaultRates.length > 0) {
    perUnit = formState.value.defaultRates[0].perUnit
  }

  const formDefaultRate = {
    ID: null,
    defaultCost: null,
    perQuantity: null,
    perUnit: perUnit,
    channelDefaultRates: channelFacilities.value.map((channelFacility) => {
      return {
        ID: null,
        channelCost: null,
        channelFacility: channelFacility,
      }
    }),
  }
  formState.value.defaultRates.push(formDefaultRate)
}

function deleteDefaultRate(index) {
  formState.value.defaultRates.splice(index, 1)
}

// expose v$, so the parent can access it
// defineExpose({ v$ })

</script>

<style lang="scss">

.rate-set {

  .rate-set-item {

    display: flex;

    & + .rate-set-item {
      margin-top: 20px;
      border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
      padding-top: 20px;
    }

    > :not(:first-child) {
      padding-left: 10px;
    }

    > :not(:last-child) {
      padding-right: 10px;
    }

    .rate-set-item-id {
      flex: 0 0 100px;
    }

    .rate-set-item-per-quantity,
    .rate-set-item-per-unit {
      flex: 0 1 150px;
    }

    .rate-set-item-cost {
      flex-grow: 1;
    }

    .rate-set-item-expand {
      flex: 0 0;
    }

    .rate-set-item-error {
      flex: 1 0;
      color: rgb(var(--v-theme-error));
    }

    .rate-set-item-tool {
      flex: 0 0;
      margin-left: auto;
    }

  }

}

</style>
